import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore, useNotifications } from '../../stores/notificationStore';
import NotificationItem from './NotificationItem';
import { useTranslation } from 'react-i18next';

interface NotificationDropdownProps {
    onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
    const { t } = useTranslation('notification');
    const navigate = useNavigate();
    const notifications = useNotifications();
    const { loading, hasMore, page, fetchNotifications, markAllAsRead } = useNotificationStore();

    // Fetch notifications when dropdown opens
    useEffect(() => {
        fetchNotifications(1);
    }, [fetchNotifications]);

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            fetchNotifications(page + 1);
        }
    };

    const handleMarkAllRead = async () => {
        await markAllAsRead();
    };

    const handleViewAll = () => {
        onClose();
        navigate('/notifications');
    };

    return (
        <div className="fixed top-14 left-3 right-3 sm:absolute sm:top-auto sm:left-auto sm:right-0 mt-2 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-900">
                    {t('title')}
                </h3>
                <button
                    onClick={handleMarkAllRead}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                    {t('markAllAsRead')}
                </button>
            </div>

            {/* Notification List */}
            <div className="flex-1 overflow-y-auto">
                {loading && notifications.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent" />
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 px-4 text-gray-500">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-12 w-12 mb-3 text-gray-300"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                        </svg>
                        <p className="text-sm">
                            {t('noNotifications')}
                        </p>
                    </div>
                ) : (
                    <>
                        {notifications.map((notification) => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onClose={onClose}
                            />
                        ))}

                        {/* Load More Button */}
                        {hasMore && (
                            <div className="px-4 py-3 border-t border-gray-100">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                    className="w-full py-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center">
                                            <span className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent mr-2" />
                                            {t('common:loading')}
                                        </span>
                                    ) : (
                                        t('common:seeMore')
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                <button
                    onClick={handleViewAll}
                    className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                    {t('viewAll')}
                </button>
            </div>
        </div>
    );
};

export default NotificationDropdown;
